/** Small parser for pdfTeX logs: pull out the first real error and its line. */

export interface ParsedError {
  /** The `!`-prefixed error message, without the marker. */
  message: string;
  /** 1-based source line, when the log names one. */
  line?: number;
  /** A friendlier explanation for common failures, when we recognize one. */
  hint?: string;
}

const MISSING_FILE = /LaTeX Error: File [`']([^']+)' not found/;
const FILE_LINE_ERROR = /^\.?\/?[\w./-]+\.tex:(\d+):\s*(.*)$/;

/**
 * Extract the first error from a pdfTeX log. Handles both classic
 * `! message` + `l.42` pairs and `-file-line-error` style `main.tex:42:`
 * prefixes. Returns null when the log carries no recognizable error.
 */
export function parseFirstError(log: string): ParsedError | null {
  const lines = log.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i] ?? '';

    const fileLine = FILE_LINE_ERROR.exec(raw);
    if (fileLine?.[1]) {
      const message = (fileLine[2] ?? '').trim() || 'Unknown error';
      return withHint({ message, line: Number(fileLine[1]) });
    }

    if (!raw.startsWith('!')) continue;
    const message = raw.replace(/^!\s*/, '').trim() || 'Unknown error';
    // The `l.<n>` line usually follows within a few lines of the message.
    for (let j = i + 1; j < Math.min(i + 12, lines.length); j++) {
      const m = /^l\.(\d+)/.exec(lines[j] ?? '');
      if (m?.[1]) return withHint({ message, line: Number(m[1]) });
    }
    return withHint({ message });
  }
  return null;
}

function withHint(err: ParsedError): ParsedError {
  const missing = MISSING_FILE.exec(err.message);
  if (missing?.[1]) {
    const file = missing[1];
    return {
      ...err,
      hint:
        `"${file}" is not part of the package set bundled with this site. ` +
        'Remove the reference, or use one of the supported packages.',
    };
  }
  return err;
}

/** The informative tail of a log, for display under the error summary. */
export function logTail(log: string, maxLines = 40): string {
  return log.split('\n').slice(-maxLines).join('\n').trim();
}
